import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { CivilDate } from 'tc39-proposal-temporal';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import {
  compareCivilDate,
  getCivilDateDayOfWeek,
  isCivilDateInRange,
  getCivilDateMonthStart,
  getCivilDateMonthEnd,
  isCivilDateRangeStartOrEnd,
  getThisMonday
} from '@bingads-webui-universal/temporal-utitlities';
import { WithArrowKeys } from '@bingads-webui-react/hoc-with-arrow-keys';
import { keyCode } from '@bingads-webui/keycode';
import { DayItem } from './day-item';
import { CalendarMonth } from './calendar-month';
import './month-calender.less';

const DAYS_IN_WEEK = 7;
const formatMonthDayYear = (date, i18n) =>
  `${i18n.formatCivilDate(date, { skeleton: 'yMd' })}`;
const formatMonthYear = (date, i18n) =>
  `${i18n.formatCivilDate(date, { skeleton: 'yMMM' })}`;
const formatDay = (date, i18n) =>
  `${i18n.formatCivilDate(date, { skeleton: 'd' })}`;
const weekDays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const convertCivilTimeToDate = date => new Date(date.year, date.month - 1, date.day);


/*  eslint-disable jsx-a11y/no-noninteractive-tabindex */
class SingleMonthCalendarInternal extends React.PureComponent {
  static propTypes = {
    defaultSelectedDate: PropTypes.instanceOf(CivilDate),
    defaultNavigatedDate: PropTypes.instanceOf(CivilDate),
    defaultSelectedRanges: PropTypes.arrayOf(PropTypes.shape({
      start: PropTypes.instanceOf(CivilDate),
      end: PropTypes.instanceOf(CivilDate),
    })),
    selectedDate: PropTypes.instanceOf(CivilDate),
    navigatedDate: PropTypes.instanceOf(CivilDate),
    selectedRanges: PropTypes.arrayOf(PropTypes.shape({
      start: PropTypes.instanceOf(CivilDate),
      end: PropTypes.instanceOf(CivilDate),
    })),
    minDate: PropTypes.instanceOf(CivilDate),
    maxDate: PropTypes.instanceOf(CivilDate),
    today: PropTypes.instanceOf(CivilDate),
    isLeftNavigateVisible: PropTypes.bool,
    isRightNavigateVisible: PropTypes.bool,
    onSelectDate: PropTypes.func,
    onNavigateDate: PropTypes.func,
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
      formatCivilDate: PropTypes.func.isRequired,
      getToday: PropTypes.func.isRequired,
      getDayOfWeek: PropTypes.func.isRequired,
      getFirstDayOfWeek: PropTypes.func.isRequired,
    }).isRequired,
    observable: PropTypes.shape({
      subscribe: PropTypes.func.isRequired,
    }).isRequired,
    isMonthPickerEnabled: PropTypes.bool,
    isMonthRangePicker: PropTypes.bool,
    isAriaLabelTranslated: PropTypes.bool,
  }

  static defaultProps = {
    defaultSelectedDate: undefined,
    defaultNavigatedDate: undefined,
    defaultSelectedRanges: [],
    minDate: undefined,
    maxDate: undefined,
    today: undefined,
    selectedDate: undefined,
    navigatedDate: undefined,
    selectedRanges: [],
    isLeftNavigateVisible: true,
    isRightNavigateVisible: true,
    onNavigateDate: undefined,
    onSelectDate: undefined,
    isMonthPickerEnabled: false,
    isMonthRangePicker: false,
    isAriaLabelTranslated: false,
  }

  constructor(props) {
    super(props);
    const navigatedDate = props.navigatedDate || props.defaultNavigatedDate
      || props.today || props.i18n.getToday();
    const selectedDate = props.selectedDate || props.defaultSelectedDate;
    this.state = {
      navigatedDate,
      selectedDate,
      focusedDate: null,
      showMonthPicker: false,
      prevPropsNavigatedDate: navigatedDate,
      weeks: this.getWeeks(props, navigatedDate, selectedDate),
    };

    const {
      observable,
    } = props;

    let targetDate = null;
    const observer = {
      next: (data) => {
        if (this.state.showMonthPicker && this.props.isMonthPickerEnabled) {
          return;
        }

        switch (data) {
          case keyCode.ArrowUp:
            targetDate = this.addDay(this.state.focusedDate, -7);
            this.setState({
              focusedDate: targetDate,
            });
            break;
          case keyCode.ArrowDown:
            targetDate = this.addDay(this.state.focusedDate, 7);
            this.setState({
              focusedDate: targetDate,
            });
            break;
          case keyCode.ArrowLeft:
            targetDate = this.addDay(this.state.focusedDate, -1);
            this.setState({
              focusedDate: targetDate,
            });
            break;
          case keyCode.ArrowRight:
            targetDate = this.addDay(this.state.focusedDate, 1);
            this.setState({
              focusedDate: targetDate,
            });
            break;
          default:
            break;
        }
      },
    };
    observable.subscribe(observer);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.navigatedDate) {
      if (
        compareCivilDate(nextProps.navigatedDate, prevState.prevPropsNavigatedDate) !== 0
      ) {
        return {
          navigatedDate: nextProps.navigatedDate,
          prevPropsNavigatedDate: nextProps.navigatedDate,
        };
      }
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(prevProps, this.props) ||
      (this.state.navigatedDate && prevState.navigatedDate &&
        compareCivilDate(prevState.navigatedDate, this.state.navigatedDate) !== 0) ||
      (this.state.selectedDate && prevState.selectedDate &&
        compareCivilDate(prevState.selectedDate, this.state.selectedDate) !== 0)
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        weeks: this.getWeeks(this.props, this.state.navigatedDate, this.state.selectedDate),
      });
    }
  }

  onSelectDate = (selectedDate) => {
    if (this.props.onSelectDate) {
      this.props.onSelectDate(selectedDate);
    }
    // Navigate to next or previous month if needed
    if (!this.props.isMonthRangePicker && selectedDate.month !== this.state.navigatedDate.month) {
      const compareResult = compareCivilDate(selectedDate, this.state.navigatedDate);
      if (compareResult < 0) {
        this.onNavigateToPrevMonth();
      } else if (compareResult > 0) {
        this.onNavigateToNextMonth();
      }
    }
    this.setState({
      selectedDate,
      focusedDate: convertCivilTimeToDate(selectedDate),
      weeks: this.getWeeks(this.props, this.state.navigatedDate, selectedDate),
    });
  }

  onSelectMonth = (selectedMonth, selectedYear) => {
    const newNavigatedDate = new CivilDate(selectedYear, selectedMonth + 1, 1);
    this.setState({
      showMonthPicker: false,
      focusedDate: convertCivilTimeToDate(newNavigatedDate),
    });
    this.updateNavigateDate(newNavigatedDate);
  }

  onNavigateToPrevMonth = () => {
    const newNavigateDate = this.state.navigatedDate.plus({ months: -1 });
    this.updateNavigateDate(newNavigateDate);
  }

  onNavigateToNextMonth = () => {
    const newNavigateDate = this.state.navigatedDate.plus({ months: 1 });
    this.updateNavigateDate(newNavigateDate);
  }

  getWeeks(props, navigatedDate, selectedDate) {
    const {
      minDate,
      maxDate,
      today,
      i18n,
      selectedRanges,
      isMonthRangePicker,
    } = props;

    if (isMonthRangePicker) { return []; }

    const todayDate = today || i18n.getToday();
    let date = new CivilDate(navigatedDate.year, navigatedDate.month, 1);
    const weeks = [];

    // Cycle the date backwards to get to the first day of the week.
    // For US the first day of the week is Sunday, and some counties like UK DE, first day is Monday
    const firstDayOfMonthIndex = weekDays.indexOf(i18n.getFirstDayOfWeek());
    date = firstDayOfMonthIndex === 1 ?
      getThisMonday(date) :
      date.plus({ days: -getCivilDateDayOfWeek(date) + firstDayOfMonthIndex });

    let isAllDaysOfWeekOutOfMonth = false;
    let shouldGetWeeks = true;

    let foundFirstDayOfMonth = false;

    for (let weekIndex = 0; shouldGetWeeks; weekIndex += 1) {
      const week = [];

      isAllDaysOfWeekOutOfMonth = true;

      for (let dayIndex = 0; dayIndex < DAYS_IN_WEEK; dayIndex += 1) {
        const isInMonth = (date.month === navigatedDate.month);
        let isFirstDayOfMonth = false;

        if (isInMonth && !foundFirstDayOfMonth) {
          foundFirstDayOfMonth = true;
          isFirstDayOfMonth = true;
        }

        const originalDate = date;
        const dayInfo = {
          key: date.toString(),
          date: date.day,
          originalDate,
          isInMonth,
          isToday: date ? compareCivilDate(todayDate, date) === 0 : false,
          isSelected: selectedDate && date ? compareCivilDate(date, selectedDate) === 0 : false,
          onSelected: this.onSelectDate.bind(this, originalDate),
          isInBounds: (!minDate || compareCivilDate(minDate, date) <= 0) &&
            (!maxDate || compareCivilDate(date, maxDate) <= 0),
          formattedDate: new Date(date.year, date.month - 1, date.day),
          isSelectedRangeStart: selectedRanges[0] ?
            compareCivilDate(date, selectedRanges[0].start) === 0 : false,
          isSelectedRangeEnd: selectedRanges[0] ?
            compareCivilDate(date, selectedRanges[0].end) === 0 : false,
          isFirstDayOfMonth,
        };

        week.push(dayInfo);

        if (dayInfo.isInMonth) {
          isAllDaysOfWeekOutOfMonth = false;
        }

        date = date.plus({ days: 1 });
      }

      shouldGetWeeks = !isAllDaysOfWeekOutOfMonth || weekIndex <= 5;
      if (shouldGetWeeks) {
        weeks.push(week);
      }
    }

    return weeks;
  }

  getDayClass(day) {
    const [selectedRange, comparedRange] = this.props.selectedRanges.length > 0 ?
      this.props.selectedRanges : this.props.defaultSelectedRanges;
    return classNames({
      isSelected: day.isSelected,
      isInMonth: day.isInMonth,
      notInMonth: !day.isInMonth,
      isInBounds: day.isInBounds,
      notInBounds: !day.isInBounds,
      isToday: day.isToday,
      isInSelectedRange: this.isInSelectedRange(day),
      isInComparedRange: comparedRange && comparedRange.start && comparedRange.end
        && isCivilDateInRange(day.originalDate, comparedRange),
      isSelectedRangeStartOrEnd: selectedRange && selectedRange.start && selectedRange.end
        && isCivilDateRangeStartOrEnd(day.originalDate, selectedRange),
      isComparedRangeStartOrEnd: comparedRange && comparedRange.start && comparedRange.end
        && isCivilDateRangeStartOrEnd(day.originalDate, comparedRange),
    });
  }

  isInSelectedRange(day) {
    const [selectedRange] = this.props.selectedRanges.length > 0 ?
      this.props.selectedRanges : this.props.defaultSelectedRanges;

    return selectedRange && selectedRange.start && selectedRange.end
      && isCivilDateInRange(day.originalDate, selectedRange);
  }

  addDay = (date, days) => {
    if (date === null) {
      // eslint-disable-next-line no-param-reassign
      date = convertCivilTimeToDate(this.props.today);
    }
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    if (result.getMonth() !== date.getMonth()) {
      if (days > 0) {
        this.onNavigateToNextMonth();
      } else {
        this.onNavigateToPrevMonth();
      }
    }
    return result;
  }

  updateNavigateDate = (newNavigateDate) => {
    this.setState({
      navigatedDate: newNavigateDate,
      weeks: this.getWeeks(this.props, newNavigateDate, this.state.selectedDate),
    });
    if (this.props.onNavigateDate) {
      this.props.onNavigateDate(newNavigateDate, false);
    }
  }

  focusItemCallback = (date) => {
    this.setState({
      focusedDate: date,
    });
  }

  handleMonthPickerClick = () => {
    this.setState({
      showMonthPicker: true,
    });
  }

  renderMonthLevelCalendarMonth = ({
    i18n,
    maxDate,
    selectedDate,
    minDate,
    isLeftNavigateVisible,
    isRightNavigateVisible,
  }) => (
    <CalendarMonth
      i18n={i18n}
      onSelectMonth={(month, year) => this.onSelectDate(new CivilDate(year, month + 1, 1))}
      selectedDate={selectedDate}
      minDate={minDate}
      maxDate={maxDate}
      navigatedDate={this.state.navigatedDate}
      isLeftNavigateVisible={isLeftNavigateVisible}
      isRightNavigateVisible={isRightNavigateVisible}
      selectedRanges={this.props.selectedRanges}
      defaultSelectedRanges={this.props.defaultSelectedRanges}
      isMonthRangePicker={this.props.isMonthRangePicker}
      onNavigateDate={this.updateNavigateDate}
    />
  );

  renderDateLevelCalendar = ({
    showMonthPicker,
    isMonthPickerEnabled,
    i18n,
    maxDate,
    selectedDate,
    minDate,
    isLeftNavigateVisible,
    prevMonthInBounds,
    monthYear,
    nextMonthYear,
    prevMonthYear,
    isRightNavigateVisible,
    nextMonthInBounds,
    isAriaLabelTranslated,
    curWeekDays,
  }) => (
    showMonthPicker && isMonthPickerEnabled
      ? <CalendarMonth
        i18n={i18n}
        onSelectMonth={this.onSelectMonth}
        selectedDate={selectedDate}
        minDate={minDate}
        maxDate={maxDate}
      />
      : (
        <div className="single-month-calendar">
          <div className="header">
            <div className="monthComponents">
              <div className="navContainer" aria-live="polite">
                {isLeftNavigateVisible && (
                  <div
                    className="prevMonth"
                    tabIndex="0"
                    role="button"
                    aria-label={isAriaLabelTranslated ? i18n.getString(_TL_('Previous month {{prevMonthYear}}'), { prevMonthYear }) : `Previous month ${prevMonthYear}`}
                    disabled={!prevMonthInBounds}
                    onClick={prevMonthInBounds ? this.onNavigateToPrevMonth : undefined}
                    onKeyDown={prevMonthInBounds ?
                      event => submitEvent(this.onNavigateToPrevMonth)({ keyCode: event.which })
                      : undefined}
                  >
                    <span className="glyphicon glyphicon-menu-left" />
                  </div>
                )}
                <div className="monthAndYear">
                  {
                    isMonthPickerEnabled ?
                      <div
                        role="button"
                        tabIndex="0"
                        onClick={this.handleMonthPickerClick}
                        onKeyDown={
                          event =>
                            submitEvent(this.handleMonthPickerClick)({ keyCode: event.which })
                        }
                        className={isMonthPickerEnabled ? 'monthYearButton' : ''}
                      >
                        {monthYear}
                      </div>
                      : <span> {monthYear} </span>
                  }
                </div>
                {isRightNavigateVisible && (
                  <div
                    className="nextMonth"
                    tabIndex="0"
                    role="button"
                    aria-label={isAriaLabelTranslated ? i18n.getString(_TL_('Next month {{nextMonthYear}}'), { nextMonthYear }) : `Next month ${nextMonthYear}`}
                    disabled={!nextMonthInBounds}
                    onClick={nextMonthInBounds ? this.onNavigateToNextMonth : undefined}
                    onKeyDown={nextMonthInBounds ?
                      event => submitEvent(this.onNavigateToNextMonth)({ keyCode: event.which })
                      : undefined}
                  >
                    <span className="glyphicon glyphicon-menu-right" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <table className="table" role="grid" >
              <thead>
                <tr>
                  {curWeekDays.map((val, index) => (
                    <th
                      className="weekday"
                      key={index.toString()}
                      role="columnheader"
                      scope="col"
                      title={i18n.getDayOfWeek(val, { style: 'wide' })}
                      aria-label={i18n.getDayOfWeek(val, { style: 'wide' })}
                    >
                      {i18n.getDayOfWeek(val, { style: 'narrow' })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {this.state.weeks.map((week, index) => (
                  <tr
                    role="row"
                    key={index.toString()}
                  >
                    {week.map((day, dayIndex) => {
                      const weekIdx = index;
                      const dayIdx = dayIndex;
                      return (
                        <DayItem
                          key={(weekIdx * 7) + dayIdx}
                          i18n={i18n}
                          day={day}
                          date={day.formattedDate}
                          formatMonthDayYear={formatMonthDayYear}
                          formatDay={formatDay}
                          isFocused={
                            !!(this.state.focusedDate
                              && (day.formattedDate.getTime()
                                === this.state.focusedDate.getTime()))
                          }
                          dayClass={this.getDayClass(day)}
                          focusItemCallback={this.focusItemCallback}
                          isInSelectedRange={this.isInSelectedRange(day)}
                        />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )
  )

  render() {
    const {
      navigatedDate,
      showMonthPicker,
      selectedDate,
    } = this.state;
    const {
      minDate,
      maxDate,
      isLeftNavigateVisible,
      isRightNavigateVisible,
      i18n,
      isMonthPickerEnabled,
      isMonthRangePicker,
      isAriaLabelTranslated,
    } = this.props;
    // determine if previous/next months are in bounds
    const prevMonthInBounds = !minDate ||
      compareCivilDate(minDate, getCivilDateMonthStart(navigatedDate)) <= 0;
    const nextMonthInBounds = !maxDate ||
      compareCivilDate(getCivilDateMonthEnd(navigatedDate), maxDate) <= 0;
    const monthYear = formatMonthYear(navigatedDate, i18n);
    const prevMonth = new CivilDate(navigatedDate.year, navigatedDate.month - 1, 1);
    const prevMonthYear = formatMonthYear(prevMonth, i18n);
    const nextMonth = new CivilDate(navigatedDate.year, navigatedDate.month + 1, 1);
    const nextMonthYear = formatMonthYear(nextMonth, i18n);
    // Apply first day of week from i18n for countries
    const firstDayOfMonthIndex = weekDays.indexOf(i18n.getFirstDayOfWeek());
    const curWeekDays =
    firstDayOfMonthIndex ?
      weekDays.slice(firstDayOfMonthIndex).concat(weekDays.slice(0, firstDayOfMonthIndex)) :
      weekDays;

    const props = {
      showMonthPicker,
      isMonthPickerEnabled,
      i18n,
      maxDate,
      selectedDate,
      minDate,
      isLeftNavigateVisible,
      prevMonthInBounds,
      monthYear,
      nextMonthYear,
      prevMonthYear,
      isRightNavigateVisible,
      nextMonthInBounds,
      isAriaLabelTranslated,
      curWeekDays,
    };

    return isMonthRangePicker
      ? this.renderMonthLevelCalendarMonth(props)
      : this.renderDateLevelCalendar(props);
  }
}

export const SingleMonthCalendar = WithArrowKeys(SingleMonthCalendarInternal);
